import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/Sidebar';
import EstadisticasFilters from '../components/estadisticas/EstadisticasFilters';
import EstadisticasReport from '../components/estadisticas/EstadisticasReport';
import axios from '../services/axios';
import {
  FILTROS_ESTADISTICAS_INICIALES,
  crearFechaReporte,
  crearParamsFecha,
  describirPeriodo,
} from '../utils/estadisticasReport';

function Estadisticas() {
  const [data, setData] = useState(null);
  const [tendencias, setTendencias] = useState(null);
  const [deudaPorZona, setDeudaPorZona] = useState([]);
  const [topDeudaClientes, setTopDeudaClientes] = useState([]);
  const [filtros, setFiltros] = useState(FILTROS_ESTADISTICAS_INICIALES);
  const [filtrosAplicados, setFiltrosAplicados] = useState(FILTROS_ESTADISTICAS_INICIALES);
  const [fechaReporte, setFechaReporte] = useState(crearFechaReporte());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const formatoMoneda = useMemo(
    () =>
      new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 2,
      }),
    [],
  );

  const periodoLabel = useMemo(
    () => describirPeriodo(filtrosAplicados),
    [filtrosAplicados],
  );

  const cargarTodo = useCallback(async (filtrosConsulta) => {
    if (
      filtrosConsulta.desde &&
      filtrosConsulta.hasta &&
      filtrosConsulta.desde > filtrosConsulta.hasta
    ) {
      setError('El filtro "Desde" no puede ser posterior al filtro "Hasta".');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const paramsFecha = crearParamsFecha(filtrosConsulta);
      const [resumenRes, tendenciasRes, deudaZonaRes, topClientesRes] =
        await Promise.all([
          axios.get('/estadisticas/resumen', {
            params: paramsFecha,
          }),
          axios.get('/estadisticas/tendencias', {
            params: {
              meses: 12,
              ...paramsFecha,
            },
          }),
          axios.get('/estadisticas/deuda-por-zona', {
            params: {
              limit: 10,
              ...paramsFecha,
            },
          }),
          axios.get('/estadisticas/top-deuda-clientes', {
            params: {
              limit: 20,
              ...paramsFecha,
            },
          }),
        ]);

      setData(resumenRes.data);
      setTendencias(tendenciasRes.data);
      setDeudaPorZona(deudaZonaRes.data || []);
      setTopDeudaClientes(topClientesRes.data || []);
      setFiltrosAplicados(filtrosConsulta);
      setFechaReporte(crearFechaReporte());
    } catch (err) {
      setError('No se pudieron cargar las estadísticas. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarTodo(FILTROS_ESTADISTICAS_INICIALES);
  }, [cargarTodo]);

  const aplicarFiltros = () => {
    cargarTodo(filtros);
  };

  const limpiarFiltros = () => {
    setFiltros(FILTROS_ESTADISTICAS_INICIALES);
    cargarTodo(FILTROS_ESTADISTICAS_INICIALES);
  };

  const imprimirReporte = () => {
    setFechaReporte(crearFechaReporte());
    window.setTimeout(() => window.print(), 50);
  };

  return (
    <div className="estadisticas-page flex min-h-screen bg-background-light dark:bg-background-dark">
      <Sidebar />
      <main className="flex-1 p-6 md:p-8 overflow-y-auto">
        <div className="estadisticas-no-print flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Panel de Estadísticas
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              Reporte operativo de cobros, boletas, deuda, medidores y estados de servicio.
            </p>
          </div>
          <div className="bg-primary/10 text-primary rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2 w-fit">
            <span className="material-symbols-outlined">query_stats</span>
            Actualizado: {fechaReporte}
          </div>
        </div>

        <EstadisticasFilters
          filtros={filtros}
          onChange={setFiltros}
          onApply={aplicarFiltros}
          onClear={limpiarFiltros}
          onPrint={imprimirReporte}
          periodoLabel={periodoLabel}
          loading={loading}
          printDisabled={loading || Boolean(error) || !data}
        />

        {loading ? (
          <div className="flex items-center gap-3 text-slate-500 dark:text-slate-300">
            <span className="material-symbols-outlined animate-spin">progress_activity</span>
            Cargando estadísticas...
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 px-4 py-3 rounded-lg flex items-center gap-2">
            <span className="material-symbols-outlined">error</span>
            {error}
          </div>
        ) : (
          <EstadisticasReport
            data={data}
            tendencias={tendencias}
            deudaPorZona={deudaPorZona}
            topDeudaClientes={topDeudaClientes}
            filtrosAplicados={filtrosAplicados}
            periodoLabel={periodoLabel}
            fechaReporte={fechaReporte}
            formatoMoneda={formatoMoneda}
          />
        )}
      </main>
    </div>
  );
}

export default Estadisticas;
